<sui-dialog-simple>
  <ng-template suiDialogHeader>
    <h2 ngpDialogTitle>
      {{ "move-user-company.title.move-user-to-another-company" | translate }}
    </h2>

    <sui-button
      size="sm"
      variant="danger"
      [leadingIcon]="['fas', 'times']"
      [iconOnly]="true"
      ariaLabel="Close"
      (click)="cancel()"
    ></sui-button>
  </ng-template>

  <ng-template suiDialogContent>
    <div class="move-user-company">
      <dl class="move-user-company__facts">
        <div class="move-user-company__fact">
          <dt>{{ "move-user-company.label.user" | translate }}</dt>
          <dd>
            <span class="move-user-company__name">{{ user.display_name }}</span>
            <span class="move-user-company__email">{{ user.email }}</span>
          </dd>
        </div>
        <div class="move-user-company__fact">
          <dt>{{ "move-user-company.label.current-company" | translate }}</dt>
          <dd>{{ user.company_name }}</dd>
        </div>
      </dl>

      <sui-form-field>
        <label sui-label for="move-user-company-target">
          {{ "move-user-company.label.target-company" | translate }}
        </label>
        <sui-combobox
          [id]="'move-user-company-target'"
          [formControl]="targetCompanyControl"
          [options]="companyOptions()"
          [loading]="companiesLoading()"
          [placeholder]="
            'move-user-company.placeholder.select-a-company' | translate
          "
        ></sui-combobox>
      </sui-form-field>

      @if (report(); as report) {
        <section class="move-user-company__section">
          <h3 class="move-user-company__heading">
            {{ "move-user-company.section.owned-objects" | translate }}
          </h3>
          @if (ownedObjectRows().length > 0) {
            <div class="move-user-company__table">
              <div class="move-user-company__row move-user-company__row--head">
                <span class="move-user-company__cell--label">
                  {{ "move-user-company.column.object-class" | translate }}
                </span>
                <span class="move-user-company__cell--num">
                  {{ "move-user-company.column.active" | translate }}
                </span>
                <span class="move-user-company__cell--num">
                  {{ "move-user-company.column.archived" | translate }}
                </span>
                <span class="move-user-company__cell--num">
                  {{ "move-user-company.column.deleted" | translate }}
                </span>
              </div>
              @for (row of ownedObjectRows(); track row.key) {
                <div class="move-user-company__row">
                  <span class="move-user-company__cell--label">
                    {{ "move-user-company.class." + row.labelKey | translate }}
                  </span>
                  <span class="move-user-company__cell--num">{{
                    row.active
                  }}</span>
                  <span class="move-user-company__cell--num">{{
                    row.archived
                  }}</span>
                  <span class="move-user-company__cell--num">{{
                    row.deleted
                  }}</span>
                </div>
              }
            </div>
          } @else {
            <p class="move-user-company__empty">
              {{ "move-user-company.text.no-owned-objects" | translate }}
            </p>
          }
        </section>

        <section class="move-user-company__section">
          <h3 class="move-user-company__heading">
            {{ "move-user-company.section.housekeeping" | translate }}
          </h3>
          <ul class="move-user-company__list">
            <li>
              {{
                "move-user-company.warning.sessions-signed-out"
                  | translate: { count: report.warnings.sessions }
              }}
            </li>
            <li>
              @if (report.warnings.groups.length > 0) {
                {{
                  "move-user-company.warning.groups-left"
                    | translate: { groups: groupsLeft() }
                }}
              } @else {
                {{ "move-user-company.warning.no-groups-left" | translate }}
              }
            </li>
            <li>
              {{
                "move-user-company.warning.roles-dropped-kept"
                  | translate
                    : {
                        dropped: report.warnings.roles_dropped.length,
                        kept: report.warnings.roles_kept.length
                      }
              }}
            </li>
            <li>
              {{
                "move-user-company.warning.invisible-shares"
                  | translate: { count: report.warnings.invisible_shares }
              }}
            </li>
            <li>
              {{
                "move-user-company.warning.rules-left-behind"
                  | translate: { count: report.warnings.left_behind.rule }
              }}
            </li>
          </ul>
        </section>

        @if (report.requires_new_owner) {
          <section class="move-user-company__section">
            <h3 class="move-user-company__heading">
              {{ "move-user-company.section.new-owner" | translate }}
            </h3>
            <p class="move-user-company__hint">
              {{ "move-user-company.text.new-owner-required" | translate }}
            </p>
            <div class="move-user-company__owner">
              @if (newOwner(); as owner) {
                <span class="move-user-company__owner-name">
                  {{ owner.name }}
                  @if (owner.email) {
                    <span class="move-user-company__email">{{
                      owner.email
                    }}</span>
                  }
                </span>
              } @else {
                <span class="move-user-company__empty">
                  {{ "move-user-company.text.no-owner-chosen" | translate }}
                </span>
              }
              <sui-button
                size="sm"
                variant="secondary"
                [leadingIcon]="['fas', 'user']"
                [disabled]="!!outcome()"
                (click)="chooseNewOwner()"
              >
                {{ "move-user-company.button.choose" | translate }}
              </sui-button>
            </div>
          </section>
        }
      }

      @if (outcome(); as outcome) {
        <section
          class="move-user-company__results"
          [class.move-user-company__results--failed]="!executed()"
        >
          <h3 class="move-user-company__heading">
            {{
              (executed()
                ? "move-user-company.result.user-moved"
                : "move-user-company.result.move-failed"
              ) | translate
            }}
          </h3>
          @if (outcome.report.results; as results) {
            <p>
              {{
                "move-user-company.result.applied-skipped-failed"
                  | translate
                    : {
                        applied: results.applied,
                        skipped: results.skipped,
                        failed: results.failed
                      }
              }}
            </p>
            @if (results.failures.length > 0) {
              <ul class="move-user-company__list">
                @for (failure of results.failures; track failure.action_id) {
                  <li>
                    <span class="move-user-company__failure-object">{{
                      failure.object_id
                    }}</span>
                    {{ failure.reason }}
                  </li>
                }
              </ul>
            }
          }
        </section>
      }
    </div>
  </ng-template>

  <ng-template suiDialogFooter>
    @if (!outcome()) {
      <sui-button size="sm" variant="secondary" (click)="cancel()">
        {{ "btn.cancel" | translate }}
      </sui-button>
      @if (!report()) {
        <sui-button
          size="sm"
          variant="primary"
          [disabled]="!canPlan()"
          [loading]="planMutation.isPending()"
          (click)="plan()"
        >
          {{ "move-user-company.button.plan" | translate }}
        </sui-button>
      } @else {
        <sui-button
          size="sm"
          variant="primary"
          [leadingIcon]="['fas', 'exchange-alt']"
          [disabled]="!canMove()"
          [loading]="executeMutation.isPending()"
          (click)="move()"
        >
          {{ "move-user-company.button.move" | translate }}
        </sui-button>
      }
    } @else {
      <sui-button size="sm" variant="primary" (click)="finish()">
        {{ "btn.close" | translate }}
      </sui-button>
    }
  </ng-template>
</sui-dialog-simple>
